import { z } from 'zod';

import {
  auditFieldsSchema,
  entityIdSchema,
  isoDateSchema,
  isoDateTimeSchema,
  pageMetaSchema,
  paginationQuerySchema,
} from './common.js';

export const RESOURCE_IDENTITY_AUTHORITY = {
  personnelTypes: ['guide', 'cook', 'guardian'],
  materialCategories: ['boat', 'motor', 'car', 'truck'],
  states: ['active', 'inactive', 'archived'],
  assignmentRoles: ['guide', 'cook', 'guardian', 'boat', 'motor', 'vehicle'],
} as const;

export const personnelResourceTypeSchema = z.enum(RESOURCE_IDENTITY_AUTHORITY.personnelTypes);
export const vehicleCategorySchema = z.enum(RESOURCE_IDENTITY_AUTHORITY.materialCategories);
export const resourceStateSchema = z.enum(RESOURCE_IDENTITY_AUTHORITY.states);
export const assignmentResourceTypeSchema = z.enum([
  ...RESOURCE_IDENTITY_AUTHORITY.personnelTypes,
  ...RESOURCE_IDENTITY_AUTHORITY.materialCategories,
]);
export const assignmentRoleSchema = z.enum(RESOURCE_IDENTITY_AUTHORITY.assignmentRoles);

export type PersonnelResourceType = z.infer<typeof personnelResourceTypeSchema>;
export type VehicleCategory = z.infer<typeof vehicleCategorySchema>;
export type ResourceState = z.infer<typeof resourceStateSchema>;
export type AssignmentResourceType = z.infer<typeof assignmentResourceTypeSchema>;
export type AssignmentRole = z.infer<typeof assignmentRoleSchema>;

export function isPersonnelResourceType(value: string): value is PersonnelResourceType {
  return RESOURCE_IDENTITY_AUTHORITY.personnelTypes.some((candidate) => candidate === value);
}

export function isVehicleCategory(value: string): value is VehicleCategory {
  return RESOURCE_IDENTITY_AUTHORITY.materialCategories.some((candidate) => candidate === value);
}

export function assignmentRoleForType(type: AssignmentResourceType): AssignmentRole {
  return type === 'car' || type === 'truck' ? 'vehicle' : type;
}

export function assignmentTypeMatchesRole(
  type: AssignmentResourceType,
  role: AssignmentRole,
): boolean {
  return assignmentRoleForType(type) === role;
}

export function normalizeResourceReference(value: string): string {
  return value
    .normalize('NFKC')
    .trim()
    .toLocaleUpperCase('fr-FR')
    .replace(/[\s-]+/gu, '');
}

const personnelFieldsSchema = z.object({
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120),
  email: z.union([z.email(), z.literal('')]).default(''),
  phone: z.string().trim().max(40).default(''),
  functions: z.array(personnelResourceTypeSchema).min(1).max(3),
  active: z.boolean().default(true),
  notes: z.string().trim().max(2_000).default(''),
});

export const personnelInputSchema = personnelFieldsSchema.superRefine((value, context) => {
  if (new Set(value.functions).size !== value.functions.length) {
    context.addIssue({ code: 'custom', path: ['functions'], message: 'Fonctions dupliquées.' });
  }
});

export const personnelSchema = personnelFieldsSchema.extend({
  lastName: z.string().trim().max(120),
  id: entityIdSchema,
  displayName: z.string().trim().min(1).max(241),
  state: resourceStateSchema,
  identityNeedsReview: z.boolean(),
  ...auditFieldsSchema.shape,
});

export const personnelUpdateSchema = personnelFieldsSchema.partial().extend({
  revision: z.number().int().positive(),
});

export const resourceArchiveInputSchema = z.object({
  revision: z.number().int().positive(),
});

const cursorPayloadSchema = z.object({
  label: z.string().min(1).max(300),
  id: entityIdSchema,
});

export type ResourceListCursor = z.infer<typeof cursorPayloadSchema>;

export function encodeResourceListCursor(cursor: ResourceListCursor): string {
  return encodeURIComponent(JSON.stringify(cursorPayloadSchema.parse(cursor)));
}

export function decodeResourceListCursor(cursor: string): ResourceListCursor | null {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(cursor));
    const result = cursorPayloadSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export const resourceListCursorSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => decodeResourceListCursor(value) !== null, 'Curseur Ressources invalide.');

export const personnelListQuerySchema = paginationQuerySchema.extend({
  cursor: resourceListCursorSchema.optional(),
  function: personnelResourceTypeSchema.optional(),
  state: resourceStateSchema.optional(),
});

export const personnelPageSchema = z.object({
  items: z.array(personnelSchema),
  page: pageMetaSchema,
});

const vehicleFieldsSchema = z.object({
  category: vehicleCategorySchema,
  name: z.string().trim().min(1).max(180),
  reference: z.string().trim().max(80).default(''),
  registration: z.string().trim().max(80).default(''),
  capacity: z.number().int().nonnegative().max(500).optional(),
  active: z.boolean().default(true),
  notes: z.string().trim().max(2_000).default(''),
});

function validateVehicleIdentity(
  value: z.infer<typeof vehicleFieldsSchema>,
  context: z.RefinementCtx,
): void {
  const usesReference = value.category === 'boat' || value.category === 'motor';
  if (usesReference && value.registration !== '') {
    context.addIssue({
      code: 'custom',
      path: ['registration'],
      message: 'Une embarcation ou un moteur utilise une référence, pas une immatriculation.',
    });
  }
  if (!usesReference && value.reference !== '') {
    context.addIssue({
      code: 'custom',
      path: ['reference'],
      message: 'Une voiture ou un camion utilise une immatriculation.',
    });
  }
}

export const vehicleInputSchema = vehicleFieldsSchema.superRefine(validateVehicleIdentity);

export const vehicleSchema = vehicleFieldsSchema.extend({
  id: entityIdSchema,
  state: resourceStateSchema,
  ...auditFieldsSchema.shape,
});

export const vehicleUpdateSchema = vehicleFieldsSchema.partial().extend({
  revision: z.number().int().positive(),
});

export const vehicleListQuerySchema = paginationQuerySchema.extend({
  cursor: resourceListCursorSchema.optional(),
  category: vehicleCategorySchema.optional(),
  state: resourceStateSchema.optional(),
});

export const vehiclePageSchema = z.object({
  items: z.array(vehicleSchema),
  page: pageMetaSchema,
});

export const resourceUnavailabilityInputSchema = z.object({
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  reason: z.string().trim().min(1).max(500),
  blocksAssignment: z.boolean().default(true),
});

export const resourceUnavailabilityArchiveSchema = z.object({
  revision: z.number().int().positive(),
});

export const resourceUnavailabilitySchema = z.object({
  id: entityIdSchema,
  resourceId: entityIdSchema,
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  reason: z.string().trim().max(500),
  blocksAssignment: z.boolean(),
  archivedAt: isoDateTimeSchema.nullable(),
  archivedBy: entityIdSchema.nullable(),
  ...auditFieldsSchema.shape,
});

export const resourceUnavailabilityPageSchema = z.object({
  items: z.array(resourceUnavailabilitySchema),
  page: pageMetaSchema,
});

export const stayAssignmentInputSchema = z.object({
  resourceId: entityIdSchema,
  resourceType: assignmentResourceTypeSchema,
  role: assignmentRoleSchema,
});

export const stayAssignmentArchiveSchema = z.object({
  revision: z.number().int().positive(),
});

export const assignmentWarningSchema = z.object({
  code: z.literal('RESOURCE_INFORMATIVE_UNAVAILABILITY'),
  message: z.string().min(1).max(500),
  unavailabilityId: entityIdSchema,
});

export const stayAssignmentSchema = stayAssignmentInputSchema.extend({
  id: entityIdSchema,
  stayId: entityIdSchema,
  resourceLabel: z.string().trim().min(1).max(300),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  warnings: z.array(assignmentWarningSchema),
  archivedAt: isoDateTimeSchema.nullable(),
  archivedBy: entityIdSchema.nullable(),
  ...auditFieldsSchema.shape,
});

export const stayAssignmentPageSchema = z.object({
  items: z.array(stayAssignmentSchema),
  page: pageMetaSchema,
});

export const assignableResourceStateSchema = z.enum(['available', 'warning', 'unavailable']);

export const assignableResourceSchema = z.object({
  id: entityIdSchema,
  label: z.string().min(1).max(300),
  type: assignmentResourceTypeSchema,
  state: assignableResourceStateSchema,
  warnings: z.array(assignmentWarningSchema),
});

export const assignableResourceQuerySchema = paginationQuerySchema.extend({
  cursor: resourceListCursorSchema.optional(),
  type: assignmentResourceTypeSchema,
  selectedId: entityIdSchema.optional(),
});

export const assignableResourcePageSchema = z.object({
  items: z.array(assignableResourceSchema),
  page: pageMetaSchema,
});

export const resourceHistoryEntrySchema = z.object({
  id: entityIdSchema,
  action: z.string().min(1),
  actorId: entityIdSchema.nullable(),
  occurredAt: isoDateTimeSchema,
  detail: z.string(),
});

export const personnelDetailSchema = personnelSchema.extend({
  history: z.array(resourceHistoryEntrySchema),
});

export const vehicleDetailSchema = vehicleSchema.extend({
  history: z.array(resourceHistoryEntrySchema),
});

export type Personnel = z.infer<typeof personnelSchema>;
export type PersonnelDetail = z.infer<typeof personnelDetailSchema>;
export type PersonnelInput = z.infer<typeof personnelInputSchema>;
export type PersonnelListQuery = z.infer<typeof personnelListQuerySchema>;
export type PersonnelPage = z.infer<typeof personnelPageSchema>;
export type Vehicle = z.infer<typeof vehicleSchema>;
export type VehicleDetail = z.infer<typeof vehicleDetailSchema>;
export type VehicleInput = z.infer<typeof vehicleInputSchema>;
export type VehicleListQuery = z.infer<typeof vehicleListQuerySchema>;
export type VehiclePage = z.infer<typeof vehiclePageSchema>;
export type ResourceUnavailability = z.infer<typeof resourceUnavailabilitySchema>;
export type ResourceUnavailabilityInput = z.infer<typeof resourceUnavailabilityInputSchema>;
export type StayAssignment = z.infer<typeof stayAssignmentSchema>;
export type StayAssignmentInput = z.infer<typeof stayAssignmentInputSchema>;
export type AssignableResource = z.infer<typeof assignableResourceSchema>;
export type AssignableResourceQuery = z.infer<typeof assignableResourceQuerySchema>;
export type AssignmentWarning = z.infer<typeof assignmentWarningSchema>;
